import { useId, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

// Lijst met vinkjes voor meerdere keuzes (kanalen, panden voor een voorstel, koppelvelden in
// Bewerken). Vanaf 8 opties komt er een zoekveld bij.

export function OptionList({
  options,
  value,
  onChange,
  invalid,
  labelledBy
}: {
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  invalid?: boolean;
  labelledBy?: string;
}) {
  const [query, setQuery] = useState("");
  const id = useId();
  const selected = useMemo(() => new Set(value), [value]);
  // Wat bij het openen al aangevinkt was, staat bovenaan; vinken tijdens het werken verschuift niets.
  const [initial] = useState(() => new Set(value));
  const searchable = options.length >= 8;
  const shown = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("nl-BE");
    const list = q ? options.filter((o) => o.label.toLocaleLowerCase("nl-BE").includes(q)) : options;
    return searchable ? [...list.filter((o) => initial.has(o.value)), ...list.filter((o) => !initial.has(o.value))] : list;
  }, [options, query, searchable, initial]);

  if (!options.length) return <p className="rounded-[10px] border border-dashed px-3 py-2.5 text-xs text-muted-foreground">Geen opties beschikbaar</p>;

  const toggle = (option: string, on: boolean) => onChange(on ? [...value, option] : value.filter((v) => v !== option));
  return (
    <div role="group" aria-labelledby={labelledBy} className={cn("rounded-[10px] border bg-white", invalid && "border-tone-red")}>
      {searchable && (
        <div className="relative border-b">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Zoek in ${options.length} opties…`}
            className="h-9 w-full rounded-t-[10px] bg-transparent pl-8 pr-3 text-[13px] outline-none placeholder:text-muted-foreground"
          />
        </div>
      )}
      <div className={cn("grid gap-0.5 p-1.5", searchable && "max-h-[188px] overflow-y-auto")}>
        {shown.map((option, i) => (
          <label key={option.value} htmlFor={`${id}-${i}`} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] hover:bg-surface-soft">
            <Checkbox id={`${id}-${i}`} checked={selected.has(option.value)} onCheckedChange={(on) => toggle(option.value, on === true)} />
            <span className="min-w-0 truncate">{option.label}</span>
          </label>
        ))}
        {!shown.length && <p className="px-2 py-1.5 text-xs text-muted-foreground">Niets gevonden</p>}
      </div>
      {value.length > 0 && <div className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">{value.length} gekozen</div>}
    </div>
  );
}
